import React, { PropsWithChildren } from 'react';
import { t } from '@lingui/macro';
import { noop } from 'utils/noop';
import {
    Button,
    Group,
    getClonedChild,
    Text,
    TextProps,
    ButtonProps,
    IconProps,
    Flex,
    Box,
    CloseOutlined,
    IconButtonProps,
    IconButton,
} from '@landr/maestro';
import * as Styled from './Coachmark.style';
import { CoachmarkVariantType, CoachmarkProps } from './Coachmark.types';

const Icon: React.FC<{
    icon: React.FC<IconProps>;
    variant?: CoachmarkVariantType;
}> = ({ icon, variant = 'base', ...props }) => {
    const Icon = icon;

    return (
        <Group.Item>
            <Styled.IconWrapper variant={variant}>
                <Icon />
            </Styled.IconWrapper>
        </Group.Item>
    );
};

Icon.displayName = 'Coachmark.Icon';

const Title: React.FC<PropsWithChildren<TextProps>> = ({
    children,
    ...props
}) => (
    <Group.Item>
        <Text as="h3" fontWeight="bold" textStyle="lead" {...props}>
            {children}
        </Text>
    </Group.Item>
);

Title.displayName = 'Coachmark.Title';

const Content: React.FC<PropsWithChildren<TextProps>> = ({
    children,
    ...props
}: React.PropsWithChildren<TextProps>) => (
    <Text as="p" textStyle="body" marginBottom="sm" {...props}>
        {children}
    </Text>
);

Content.displayName = 'Coachmark.Content';

interface ExitProps extends ButtonProps {
    onClick?: () => void;
}

const Exit: React.FC<PropsWithChildren<ExitProps>> = ({
    children,
    ...props
}) => {
    return (
        <Box paddingRight="md">
            <Button kind="minimal" variant="secondary" size="sm" {...props}>
                {children}
            </Button>
        </Box>
    );
};

Exit.displayName = 'Coachmark.Exit';

interface NextProps extends ButtonProps {
    coachmarkVariant?: CoachmarkVariantType;
    onClick?: () => void;
}

const Next: React.FC<PropsWithChildren<NextProps>> = ({
    children,
    coachmarkVariant,
    ...props
}) => {
    const variant = coachmarkVariant === 'base' ? 'accent' : 'secondary';

    return (
        <Flex flexGrow="10" justifyContent="flex-end">
            <Button variant={variant} size="sm" {...props}>
                {children}
            </Button>
        </Flex>
    );
};

Next.displayName = 'Coachmark.Next';

interface CloseProps extends Partial<IconButtonProps> {
    coachmarkVariant?: CoachmarkVariantType;
    onClick?: () => void;
}

const Close: React.FC<CloseProps> = ({
    coachmarkVariant,
    label = t`Close`,
    size = 'md',
    icon = CloseOutlined,
    ...props
}) => {
    return (
        <Flex flexGrow="10" justifyContent="flex-end">
            <IconButton
                label={label}
                size={size}
                icon={icon}
                kind="link"
                variant="secondary"
                {...props}
            />
        </Flex>
    );
};

Close.displayName = 'Coachmark.Close';

interface CoachmarkSubComponents {
    Icon: typeof Icon;
    Title: typeof Title;
    Content: typeof Content;
    Exit: typeof Exit;
    Next: typeof Next;
    Close: typeof Close;
}

const Coachmark: React.FC<PropsWithChildren<CoachmarkProps>> &
    CoachmarkSubComponents = ({
    children,
    anchorRef,
    placement = 'auto',
    variant = 'base',
    onClickTarget = noop,
    onClose = noop,
    isOpen = true,
    ...props
}) => {
    const childrenArray = React.Children.toArray(children);

    const offset: [number, number] = [12, 12];

    const icon = getClonedChild(childrenArray, Coachmark.Icon, {
        variant: variant,
    });
    const title = getClonedChild(childrenArray, Coachmark.Title);
    const content = getClonedChild(childrenArray, Coachmark.Content);
    const exit = getClonedChild(childrenArray, Coachmark.Exit, {
        coachmarkVariant: variant,
    });
    const next = getClonedChild(childrenArray, Coachmark.Next, {
        coachmarkVariant: variant,
    });
    const close = getClonedChild(childrenArray, Coachmark.Close, {
        coachmarkVariant: variant,
    });

    return (
        <Styled.Coachmark
            anchorRef={anchorRef}
            isOpen={isOpen}
            placement={placement}
            offset={offset}
            variant={variant}
            onClose={onClose}
            {...props}
        >
            <Styled.Header>
                <Styled.HeaderItem>
                    <Group
                        alignItems="flex-start"
                        justifyContent="flex-start"
                        gap="xs"
                    >
                        {icon}
                        {title}
                    </Group>
                </Styled.HeaderItem>
                <Styled.HeaderItem flexGrow={1}>{close}</Styled.HeaderItem>
            </Styled.Header>
            {content}
            <Flex>
                {exit}
                {next}
            </Flex>
        </Styled.Coachmark>
    );
};

Coachmark.Icon = Icon;
Coachmark.Title = Title;
Coachmark.Content = Content;
Coachmark.Exit = Exit;
Coachmark.Next = Next;
Coachmark.Close = Close;

export { Coachmark };
